import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import GraphViewer from "../../../../components/GraphPlotter/GraphViewer";
import FractionInput from "../../../../components/inputs/FractionInput";
import { sampleAnchorCurve } from "./helpers/smoothGraph";
import { randInt } from "../../temat6_zadania_uzupelniajace_maturalne/podtemat3_otwarte_types/helpers/MaturaPiecewiseFunctionUtils";

function parseValue(value) {
  if (value && typeof value === "object" && Number.isFinite(value.parsed)) return value.parsed;
  return Number(value);
}

function buildData() {
  const h = randInt(-1, 1);
  const orientation = Math.random() < 0.5 ? 1 : -1;
  const variant = randInt(0, 2);

  if (variant === 0) {
    const vertexValue = orientation > 0 ? randInt(-3, -1) : randInt(2, 4);
    const leftDistance = randInt(3, 5);
    const keepSymmetric = Math.random() < 0.15;
    let rightDistance = keepSymmetric ? leftDistance : randInt(3, 5);
    if (!keepSymmetric && rightDistance === leftDistance) {
      rightDistance = leftDistance === 5 ? 3 : leftDistance + 1;
    }
    const xL = h - leftDistance;
    const xR = h + rightDistance;
    const windowVariant = randInt(0, 2);
    const windowLeft = windowVariant === 1 ? h : xL;
    const windowRight = windowVariant === 0 ? h : xR;
    const valueAt = (x) => vertexValue + orientation * Math.abs(x - h);
    const maximumValue = Math.max(valueAt(windowLeft), valueAt(h), valueAt(windowRight));
    const anchors = [
      { x: xL, y: valueAt(xL) },
      { x: h, y: vertexValue },
      { x: xR, y: valueAt(xR) },
    ];

    return {
      graphKind: "chevron",
      windowLeft,
      windowRight,
      maximumValue,
      points: {
        A: { ...anchors[0], markerSymbol: "circle-filled", showLabel: false },
        B: {
          ...anchors[1],
          showDot: false,
          showMarker: false,
          showLabel: false,
        },
        C: { ...anchors[2], markerSymbol: "circle-filled", showLabel: false },
      },
      segments: [],
      curves: [sampleAnchorCurve(anchors, { style: "turning" })],
      xRange: [-6, 6],
      yRange: [-6, 6],
    };
  }

  const relativeXs = variant === 1 ? [-4, -1, 2, 4] : [-4, -1, 1, 4];
  const baseYs = variant === 1 ? [-2, 2, -1, 3] : [-2, 2, 2, -1];
  const verticalShift = randInt(-1, 1);
  const ys = baseYs.map((value) => orientation * value + verticalShift);
  const labels = ["A", "B", "C", "D"];
  const startIndex = randInt(0, 1);
  const endIndex = startIndex + 2;
  const windowLeft = h + relativeXs[startIndex];
  const windowRight = h + relativeXs[endIndex];
  const maximumValue = Math.max(...ys.slice(startIndex, endIndex + 1));
  const points = Object.fromEntries(labels.map((label, index) => [
    label,
    {
      x: h + relativeXs[index],
      y: ys[index],
      markerSymbol: index === 0 || index === labels.length - 1
        ? "circle-filled"
        : undefined,
      showDot: index === 0 || index === labels.length - 1,
      showMarker: index === 0 || index === labels.length - 1,
      showLabel: false,
    },
  ]));

  return {
    graphKind: variant === 1 ? "zigzag" : "plateau",
    windowLeft,
    windowRight,
    maximumValue,
    points,
    segments: [],
    curves: [sampleAnchorCurve(
      labels.map((label) => ({ x: points[label].x, y: points[label].y })),
      { style: "turning" }
    )],
    xRange: [-6, 6],
    yRange: [-6, 6],
  };
}

export function generateTask() {
  return { type: "ReadMaximumOnIntervalFromGraphTask", data: buildData() };
}

export function renderTask({ data }) {
  return (
    <div className="flex flex-col gap-3">
      <p>
        Odczytaj z wykresu największą wartość funkcji w przedziale{" "}
        <InlineMath math={`[${data.windowLeft},${data.windowRight}]`} />.
      </p>
      <div style={{ margin: "0 auto", maxWidth: 330 }}>
        <GraphViewer
          width={330}
          points={data.points}
          segments={data.segments}
          curves={data.curves}
          xRange={data.xRange}
          yRange={data.yRange}
        />
      </div>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <FractionInput
      value={userAnswer || ""}
      onChange={onChange}
      disabled={disabled}
      label="największa wartość"
      placeholder="np. 4"
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  return Math.abs(parseValue(userAnswer) - data.maximumValue) < 1e-9;
}

export function renderAnswer({ data }) {
  return <InlineMath math={`f_{\\max}=${data.maximumValue}`} />;
}

export function renderExplanation({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        <strong>Krok 1.</strong> Bierzemy pod uwagę tylko fragment wykresu nad argumentami
        z przedziału <InlineMath math={`[${data.windowLeft},${data.windowRight}]`} />.
      </p>
      <div style={{ margin: "0 auto", maxWidth: 350 }}>
        <GraphViewer
          width={350}
          points={data.points}
          segments={data.segments}
          curves={data.curves}
          equations={[{
            expr: "y=" + data.maximumValue,
            color: "#8b5cf6",
            width: 2,
            dash: "dot",
          }]}
          verticalBands={[{
            left: data.windowLeft,
            right: data.windowRight,
            color: "#8b5cf6",
            opacity: 0.14,
          }]}
          xRange={data.xRange}
          yRange={data.yRange}
        />
      </div>
      <p>
        <strong>Krok 2.</strong> Szukamy najwyżej położonego punktu tego fragmentu.
        Odczytujemy jego wysokość z osi <InlineMath math="OY" /> — pytanie dotyczy
        wartości funkcji, a nie argumentu.
      </p>
      <BlockMath math={`f_{\\max}=${data.maximumValue}`} />
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  const raw = userAnswer && typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  return raw ? <InlineMath math={`f_{\\max}=${raw}`} /> : <i>(brak odpowiedzi)</i>;
}

const ReadMaximumOnIntervalFromGraphTask = {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};

export default ReadMaximumOnIntervalFromGraphTask;
